import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { Popover } from '@headlessui/react';
import { CheckCircleIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/outline';

import Footer from '../../components/footer';
import { AuthContext } from '../../contexts/auth';
import ComboboxAutocomplete from '../../components/combobox_autocomplete';
import Fetching from '../../components/fetching';
import Empty from '../../components/empty';

import { getTipiGiustificativi, getRichiesteGiustificativi, createRichiestaGiustificativo, getShifts } from '../../services/api_dipendenti';
import { estraiMessaggiErrore } from "../../services/utils";


const createInitialFormData = (requestDay = '') => ({
  date_from: requestDay,
  date_to: requestDay,
  time_from: '',
  time_to: '',
  justification: '',
  note: '',
});

const initialRequestState = {
  success: false,
  error: false,
  message: '',
  verbose: '',
};

function formatJustificationEnd(datetimeFrom, datetimeTo) {
  const from = moment(datetimeFrom);
  const to = moment(datetimeTo);

  const nextDayMidnight = moment(from).add(1, 'day').startOf('day');

  const isNextDayMidnight =
    to.isSame(nextDayMidnight) &&
    to.format('HH:mm') === '00:00';

  return {
    date: isNextDayMidnight
      ? from.format('DD/MM/YYYY')
      : to.format('DD/MM/YYYY'),
    time: isNextDayMidnight ? '24:00' : to.format('HH:mm'),
  };
}

function formatDuration(durationInMinutes) {
  const totalMinutes = Number(durationInMinutes) || 0;
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (totalHours > 0 && remainingMinutes > 0) {
    return `${totalHours} h, ${remainingMinutes} min`;
  }

  if (totalHours > 0) {
    return `${totalHours} h`;
  }

  if (remainingMinutes > 0) {
    return `${remainingMinutes} min`;
  }

  return '0 min';
}

const ArNewJustificationRequest = () => {
  const backgroudColor = 'bg-indigo-200';
  const requestQuery = new URLSearchParams(location.search);
  const requestDay = requestQuery.get('day') || '';
  const shouldShowForm = requestQuery.get('showForm') === 'true';

  const { token } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isErrorSummary, setIsErrorSummary] = useState(false);
  const [giustificativi, setGiustificativi] = useState([]);
  const [giustificativiSummary, setGiustificativiSummary] = useState([]);
  const [giustificativoSelected, setGiustificativoSelected] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showForm, setShowForm] = useState(shouldShowForm);

  const [richiesta, setRichiesta] = useState(initialRequestState);

  const [formData, setFormData] = useState(
    createInitialFormData(requestDay)
  );
  const [formErrors, setFormErrors] = useState({});

  const messageRef = useRef(null);
  const shiftsRequestIdRef = useRef(0);

  useEffect(() => {
    if (Object.keys(formErrors).length > 0 || richiesta.error) {
      messageRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [formErrors, richiesta.error]);

  const clearFieldError = (key) => {
    setFormErrors((prevState) => {
      if (!prevState[key]) {
        return prevState;
      }

      const newErrors = { ...prevState };
      delete newErrors[key];

      return newErrors;
    });
  };

  const setFormDataValue = (key, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value,
    }));

    clearFieldError(key);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormDataValue(name, value);
  };

  const handleJRTimeChange = (field, e) => {
    let inputValue = e.target.value;

    inputValue = inputValue.replace(/\D/g, '').slice(0, 4);

    let formattedTime = inputValue.replace(
      /(\d{2})(\d{0,2})/,
      '$1:$2'
    );

    if (
      inputValue.length <= 2 &&
      e.target.value.length < formattedTime.length
    ) {
      formattedTime = formattedTime.replace(':', '');
    }

    setFormDataValue(field, formattedTime);
  };

  const handleJRTimeBlur = (field) => {
    let formattedTime = formData[field] || '';

    if (!formattedTime.trim()) {
      return;
    }

    formattedTime = formattedTime.replace(/\D/g, '');

    while (formattedTime.length < 4) {
      formattedTime += '0';
    }

    formattedTime = formattedTime.slice(0, 4);

    const hours = parseInt(formattedTime.slice(0, 2), 10);
    const minutes = parseInt(formattedTime.slice(2), 10);

    const isValidTime =
      hours >= 0 &&
      hours <= 24 &&
      minutes >= 0 &&
      minutes < 60 &&
      !(hours === 24 && minutes > 0);

    if (!isValidTime) {
      setFormDataValue(field, '');

      Swal.fire({
        icon: 'error',
        title: 'Orario non valido',
        text: 'Inserisci un orario valido entro 24:00.',
      });

      return;
    }

    const formattedInput = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

    setFormDataValue(field, formattedInput);
  };

  const fetchData = async (page = 1) => {
    if (!token) {
      return;
    }

    setIsLoading(true);
    setIsLoadingSummary(true);
    setIsError(false);
    setIsErrorSummary(false);

    try {
      const response = await getTipiGiustificativi(token);

      setGiustificativi(
        Array.isArray(response) ? response : []
      );
    } catch (error) {
      console.error('Error fetching justifications:', error);
      setGiustificativi([]);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }

    try {
      const query = {
        page_size: 10,
        page,
      };

      const response = await getRichiesteGiustificativi(
        token,
        query
      );

      setGiustificativiSummary(
        Array.isArray(response?.results)
          ? response.results
          : []
      );

      const pages = Math.ceil(
        (Number(response?.count) || 0) / 10
      );

      setTotalPages(Math.max(pages, 1));
    } catch (error) {
      console.error('Error fetching justification summary:', error);
      setGiustificativiSummary([]);
      setTotalPages(1);
      setIsErrorSummary(true);
    } finally {
      setIsLoadingSummary(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [token, currentPage]);

  useEffect(() => {
    if (!token) {
      return;
    }

    const { date_from, date_to } = formData;

    if (!date_from || !date_to) {
      setShifts([]);
      setIsLoadingShifts(false);
      return;
    }

    const from = moment(date_from, 'YYYY-MM-DD', true);
    const to = moment(date_to, 'YYYY-MM-DD', true);

    if (
      !from.isValid() ||
      !to.isValid() ||
      from.isAfter(to, 'day')
    ) {
      setShifts([]);
      setIsLoadingShifts(false);
      return;
    }

    const requestId = ++shiftsRequestIdRef.current;

    const fetchShiftsData = async () => {
      setIsLoadingShifts(true);

      try {
        const shiftsData = await getShifts(token, { start: from.format('YYYY-MM-DD'), end: to.format('YYYY-MM-DD')});

        if (requestId !== shiftsRequestIdRef.current) {
          return;
        }

        setShifts(shiftsData || []);
      } catch (error) {
        if (requestId !== shiftsRequestIdRef.current) {
          return;
        }

        console.error('Error fetching shifts:', error);
        setShifts([]);
      } finally {
        if (requestId === shiftsRequestIdRef.current) {
          setIsLoadingShifts(false);
        }
      }
    };

    fetchShiftsData();
  }, [token, formData.date_from, formData.date_to]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const resetForm = () => {
    setGiustificativoSelected(null);
    setFormData(createInitialFormData());
    setFormErrors({});
    setShifts([]);
    setRichiesta(initialRequestState);
  };

  const toggleForm = () => {
    setShowForm((prevState) => !prevState);
    resetForm();
  };

  const handleJustificationChange = (selected) => {
    const selectedValue = selected || null;

    setGiustificativoSelected(selectedValue);

    setFormData((prevState) => ({
      ...prevState,
      justification: selectedValue?.id || '',
      time_from: selectedValue?.has_hours
        ? prevState.time_from
        : '',
      time_to: selectedValue?.has_hours
        ? prevState.time_to
        : '',
    }));

    clearFieldError('justification');
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.date_from) {
      errors.date_from = 'Scegliere il giorno "Dal"';
    }
    if (!formData.date_to) {
      errors.date_to = 'Scegliere il giorno "Al"';
    }

    if (
      formData.date_from &&
      formData.date_to &&
      formData.date_from > formData.date_to
    ) {
      errors.date_to =
        'La data di fine deve essere successiva alla data di inizio';
    }

    if (!formData.justification) {
      errors.justification = 'Scegli il giustificativo. Il campo è obbligatorio';
    }

    const hasOnlyOneTime =
      Boolean(formData.time_from) !==
      Boolean(formData.time_to);

    if (hasOnlyOneTime) {
      errors.time =
        'Indica sia l’orario iniziale sia l’orario finale';
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    let formattedDateTo = formData.date_to;
    let formattedTimeTo = formData.time_to;

    let datetime_from = `${formData.date_from}T00:00:00`;
    let datetime_to = `${formattedDateTo}T23:59:59`;

    if (formData.time_from && formattedTimeTo) {
      if (formattedTimeTo === '24:00') {
        formattedTimeTo = '00:00';

        formattedDateTo = moment(formattedDateTo, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD');
      }

      datetime_from = `${formData.date_from}T${formData.time_from}:00`;
      datetime_to = `${formattedDateTo}T${formattedTimeTo}:00`;
    }

    try {
      await createRichiestaGiustificativo(token, {
        datetime_from,
        datetime_to,
        note: formData.note,
        justification: formData.justification,
      });

      setRichiesta({ success: true, error: false, message: 'Richiesta inviata correttamente', verbose: '' });

      setShowForm(false);
      setFormData(createInitialFormData());
      setGiustificativoSelected(null);
      setShifts([]);

      await fetchData(currentPage);

      setTimeout(() => {
        setRichiesta(initialRequestState);
      }, 3000);
    } catch (error) {
      setRichiesta({
        success: false,
        error: true,
        message: "Errore durante l'invio della richiesta",
        verbose: estraiMessaggiErrore(error),
      });
    }
  };

  const isButtonDisabled = giustificativi.length === 0;

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-grow mb-8">
        <Popover as="header" className={backgroudColor}>
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            <div className="relative flex flex-wrap items-center justify-center lg:justify-between">
              <div className="w-full lg:border-t lg:border-white lg:border-opacity-20">
                <div className="flex justify-center my-2 py-2">
                  <h2 className="text-center text-2xl font-semibold text-indigo-800">I tuoi giustificativi</h2>
                </div>
              </div>
            </div>
          </div>
        </Popover>
        <main className='mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8 pt-4'>
          <section>
            <div className="container-fluid w-full px-4 pt-2">
              <div className="grid grid-cols-6 gap-4 mt-2 items-center px-4 md:px-10">
                <div className="col-span-6 md:col-span-4 flex flex-wrap items-center gap-4 mb-6 mt-4">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">Inserisci una nuova richiesta di Giustificativo</h3>
                  <button
                    type="button"
                    onClick={toggleForm}
                    className={`text-sm p-2 rounded-md font-semibold ${showForm ? 'bg-gray-200 text-gray-800 hover:bg-gray-400 hover:text-white' : 'bg-indigo-500 text-white hover:bg-indigo-400'}`}
                  >
                    {showForm ? 'Annulla' : 'Crea Richiesta'}
                  </button>
                </div>

                {richiesta.success && (
                  <div className="col-span-6 md:col-span-4" ref={messageRef}>
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
                      <span className="block sm:inline">{richiesta.message}</span>
                    </div>
                  </div>
                )}

                {(Object.keys(formErrors).length > 0 || richiesta.error) && (
                  <div className="col-span-6 md:col-span-4 space-y-2" ref={messageRef}>
                    {richiesta.error && (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <strong className="font-bold">Errore!</strong>
                        <span className="block sm:inline">{' '}{richiesta.message}</span>{richiesta.verbose && (<span className="block">{richiesta.verbose}</span>
                        )}
                      </div>
                    )}

                    {Object.keys(formErrors).length > 0 && (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <ul className="list-disc ml-5">
                          {Object.values(formErrors).map((errorMessage, index) => (
                              <li key={index}>{errorMessage}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {isLoading && <Fetching />}

            {isError && !isLoading && (
              <div className="mx-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Errore durante il caricamento dei giustificativi
              </div>
            )}

            {showForm && !isError && (
              <div className="px-4 sm:px-6 lg:px-8">
                <div className="mt-3 ring-1 ring-gray-300 mx-1 rounded-lg">
                  <form onSubmit={handleSubmit} className="pb-4 pt-2 mx-2">
                    <div className="grid grid-cols-2 md:grid-cols-8 gap-4 mt-2 items-center px-4 md:px-10">
                      <div className="col-span-2 md:col-span-2">
                        <label className="text-sm font-semibold text-gray-900">
                          Giustificativo
                        </label>
                      </div>
                      <div className="col-span-2 md:col-span-6 border border-gray-200 shadow-md rounded-lg">
                        <ComboboxAutocomplete
                          options={giustificativi.map((giustificativo) => ({
                              id: giustificativo.id, 
                              name: giustificativo.justification.description,
                              has_hours: giustificativo.justification.has_hours,
                            })
                          )}
                          bgActiveColor="bg-indigo-100"
                          textActiveColor="text-gray-900"
                          placeholder="Seleziona il giustificativo..."
                          onChange={handleJustificationChange}
                          value={giustificativoSelected}
                          positionOptions="absolute"
                        />
                      </div>

                      {giustificativi.length > 0 && (
                        <div className="col-span-2 md:col-span-8 py-3">
                          <div className="text-sm font-semibold text-gray-900 pb-3">Anteprima turno</div>
                          <div className="overflow-x-auto">
                            <div className="overflow-y-auto max-h-[40vh] rounded-lg">
                              <table className="min-w-full bg-white shadow-md border border-gray-200">
                                <thead className="bg-indigo-200 text-gray-900 sticky top-0 bg-indigo-100">
                                  <tr>
                                    <th className="py-2 px-4 text-left text-sm font-semibold" style={{ width: '20%' }}>Giorno</th>
                                    <th className="py-2 px-4 text-left text-sm font-semibold" style={{ width: '16%' }}>Inizio</th>
                                    <th className="py-2 px-4 text-left text-sm font-semibold" style={{ width: '16%' }}>Fine</th>
                                    <th className="py-2 px-4 text-left text-sm font-semibold" style={{ width: '16%' }}>Inizio Pausa</th>
                                    <th className="py-2 px-4 text-left text-sm font-semibold" style={{ width: '16%' }}>Fine Pausa</th>
                                    <th className="py-2 px-4 text-left text-sm font-semibold" style={{ width: '16%' }}>Durata</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {isLoadingShifts ? (
                                    <tr>
                                      <td
                                        colSpan={6}
                                        className="px-8 py-4 text-center text-sm text-gray-900"
                                      >
                                        Caricamento turni...
                                      </td>
                                    </tr>
                                  ) : shifts?.days?.length > 0 ? (
                                    shifts.days.map((day, dayIndex) => {
                                      const isFissoHoliday = day.shift_type === 'fisso' && day.holidays?.length > 0;

                                      const holidayStyle = isFissoHoliday
                                        ? {
                                            backgroundColor: '#f9fafb',
                                            backgroundImage: `repeating-linear-gradient(
                                              135deg,
                                              transparent,
                                              transparent 8px,
                                              rgba(156, 163, 175, 0.08) 8px,
                                              rgba(156, 163, 175, 0.08) 16px
                                            )`,
                                          }
                                        : undefined;

                                      const holidayLabel = isFissoHoliday && (
                                        <div className="mt-1 text-xs font-semibold text-red-600">
                                          {day.holidays.map(h => h.name).join(', ')}
                                        </div>
                                      );

                                      return day.shifts?.length > 0 ? (
                                        day.shifts.map((shift, shiftIndex) =>
                                          shift.time_slots?.length > 0 ? (
                                            shift.time_slots.map((slot, slotIndex) => (
                                              <tr
                                                key={`${dayIndex}-${shiftIndex}-${slotIndex}`}
                                                className="border-t"
                                                style={holidayStyle}
                                              >
                                                {shiftIndex === 0 && slotIndex === 0 && (
                                                  <td
                                                    className="py-2 px-4 text-sm text-gray-900"
                                                    rowSpan={day.shifts.reduce(
                                                      (total, currentShift) =>
                                                        total + Math.max(
                                                          currentShift.time_slots?.length || 0,
                                                          1
                                                        ),
                                                      0
                                                    )}
                                                  >
                                                    {moment(day.date).format('DD/MM/YYYY')}
                                                    {holidayLabel}
                                                  </td>
                                                )}

                                                <td className="py-2 px-4 text-sm text-gray-900" style={{ width: '16%' }}>
                                                  {slot.start_at}
                                                </td>

                                                <td className="py-2 px-4 text-sm text-gray-900" style={{ width: '16%' }}>
                                                  {slot.end_at}
                                                </td>

                                                <td className="py-2 px-4 text-sm text-gray-900" style={{ width: '16%' }}>
                                                  {slot.breaks?.map((breakItem, breakIndex) => (
                                                    <div key={breakIndex}>
                                                      {breakItem.start_at}
                                                    </div>
                                                  ))}
                                                </td>

                                                <td className="py-2 px-4 text-sm text-gray-900">
                                                  {slot.breaks?.map((breakItem, breakIndex) => (
                                                    <div key={breakIndex}>
                                                      {breakItem.end_at}
                                                    </div>
                                                  ))}
                                                </td>

                                                <td className="py-2 px-4 text-sm text-gray-900">
                                                  {formatDuration(slot.duration)}
                                                </td>
                                              </tr>
                                            ))
                                          ) : (
                                            <tr
                                              key={`${dayIndex}-${shiftIndex}`}
                                              className="border-t"
                                              style={holidayStyle}
                                            >
                                              {shiftIndex === 0 && (
                                                <td
                                                  className="py-2 px-4 text-sm text-gray-900"
                                                  rowSpan={day.shifts.reduce(
                                                    (total, currentShift) =>
                                                      total + Math.max(
                                                        currentShift.time_slots?.length || 0,
                                                        1
                                                      ),
                                                    0
                                                  )}
                                                >
                                                  {moment(day.date).format('DD/MM/YYYY')}
                                                  {holidayLabel}
                                                </td>
                                              )}

                                              <td
                                                colSpan={5}
                                                className="py-2 px-4 text-sm text-gray-600"
                                              >
                                                Nessun turno previsto
                                              </td>
                                            </tr>
                                          )
                                        )
                                      ) : (
                                        <tr
                                          key={day.date || dayIndex}
                                          className="border-t"
                                          style={holidayStyle}
                                        >
                                          <td className="py-2 px-4 text-sm text-gray-900">
                                            {moment(day.date).format('DD/MM/YYYY')}
                                            {holidayLabel}
                                          </td>

                                          <td
                                            colSpan={5}
                                            className="py-2 px-4 text-sm text-gray-600"
                                          >
                                            Nessun turno previsto
                                          </td>
                                        </tr>
                                      );
                                    })
                                  ) : (
                                    <tr>
                                      <td
                                        colSpan={6}
                                        className="px-8 py-4 text-center text-sm font-normal text-gray-900 whitespace-normal break-words"
                                      >
                                        Inserisci le date di richiesta del giustificativo per visualizzare i turni
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="col-span-2 md:col-span-1">
                        <label htmlFor="date_from" className="text-sm font-semibold text-gray-900">
                          Dal
                        </label>
                      </div>

                      <div className="col-span-2 md:col-span-3">
                        <input
                          id="date_from"
                          type="date"
                          name="date_from"
                          value={formData.date_from}
                          onChange={handleInputChange}
                          className="mt-1 p-2 w-full border border-gray-200 rounded-md shadow-md text-sm"
                        />
                      </div>

                      <div className="col-span-2 md:col-span-1">
                        <label htmlFor="date_to" className="text-sm font-semibold text-gray-900">
                          Al
                        </label>
                      </div>

                      <div className="col-span-2 md:col-span-3">
                        <input
                          id="date_to"
                          type="date"
                          name="date_to"
                          value={formData.date_to}
                          onChange={handleInputChange}
                          className="mt-1 p-2 w-full border border-gray-200 rounded-md shadow-md text-sm"
                        />
                      </div>

                      {giustificativoSelected?.has_hours && (
                        <>
                          <div className="col-span-2 md:col-span-1">
                            <label htmlFor="time_from" className="text-sm font-semibold text-gray-900">
                              Dalle ore
                            </label>
                          </div>

                          <div className="col-span-2 md:col-span-3">
                            <div className="relative inline-block w-full">
                              <input
                                id="time_from"
                                type="text"
                                placeholder="hh:mm"
                                value={formData.time_from}
                                onChange={(e) => handleJRTimeChange('time_from', e)}
                                onBlur={() => handleJRTimeBlur('time_from')}
                                className="p-2 pl-4 pr-8 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-md sm:text-sm"
                              />

                              <ClockIcon className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 w-4 h-4" />
                            </div>
                          </div>

                          <div className="col-span-2 md:col-span-1">
                            <label
                              htmlFor="time_to"
                              className="text-sm font-semibold text-gray-900"
                            >
                              Alle ore
                            </label>
                          </div>

                          <div className="col-span-2 md:col-span-3">
                            <div className="relative inline-block w-full">
                              <input
                                id="time_to"
                                type="text"
                                placeholder="hh:mm"
                                value={formData.time_to}
                                onChange={(e) => handleJRTimeChange('time_to', e)}
                                onBlur={() => handleJRTimeBlur('time_to')}
                                className="p-2 pl-4 pr-8 border-gray-200 rounded-md focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-md sm:text-sm"
                              />

                              <ClockIcon className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 w-4 h-4" />
                            </div>
                          </div>
                        </>
                      )}

                      <div className="col-span-2 md:col-span-1">
                        <label
                          htmlFor="note"
                          className="text-sm font-semibold text-gray-900"
                        >
                          Note
                        </label>
                      </div>

                      <div className="col-span-2 md:col-span-7">
                        <input
                          id="note"
                          name="note"
                          onChange={handleInputChange}
                          value={formData.note}
                          type="text"
                          className="w-full border-gray-200 rounded-md focus:ring-gray-300 focus:border-gray-300 block shadow-md sm:text-sm"
                        />
                      </div>
                    </div>

                    <div className="flex justify-center pt-5">
                      {giustificativi.length === 0 ? (
                        <div className="text-sm font-semibold text-indigo-800">
                          Non sono presenti giustificativi attivi. La richiesta non può essere inviata.
                        </div>
                      ) : (
                        <button
                          type="submit"
                          className={`text-white text-sm px-4 py-2 rounded-md focus:outline-none w-24 font-semibold ${
                            isButtonDisabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-indigo-500 hover:bg-indigo-400'
                          }`}
                          disabled={isButtonDisabled}
                        >
                          Invia
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            )}
          </section>

          <section>
            <div className="container-fluid w-full px-4 pt-2">
              <div className="grid grid-cols-6 gap-4 mt-2 items-center px-4 md:px-10">
                <div className="col-span-6 md:col-span-4">
                  <h3 className="text-base font-semibold leading-6 text-gray-900">
                    Riepilogo
                  </h3>
                </div>
              </div>
            </div>

            {isLoadingSummary && <Fetching />}

            {isErrorSummary && !isLoadingSummary && (
              <div className="mx-4 mt-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Errore durante il caricamento del riepilogo
              </div>
            )}
          </section>

          {!isLoadingSummary &&
            !isErrorSummary &&
            giustificativiSummary.length === 0 && (
              <div className="px-4">
                <Empty />
              </div>
            )}

          {!isLoadingSummary &&
            !isErrorSummary &&
            giustificativiSummary.length > 0 && (
              <div className="px-4 sm:px-6 lg:px-8">
                <div
                  className="mt-3 ring-1 ring-gray-300 mx-1 rounded-lg overflow-y-auto"
                  style={{ maxHeight: '70vh' }}
                >
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="sticky top-0 z-10 bg-white">
                      <tr>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Codice
                        </th>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Giustificativo
                        </th>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Dal
                        </th>
                        <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          Al
                        </th>
                        <th className="py-3.5 pl-4 pr-3 text-center text-sm font-semibold text-gray-900 sm:pl-6">
                          Stato
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {giustificativiSummary.map((item) => {
                        const {
                          date: endDate,
                          time: endTime,
                        } = formatJustificationEnd(
                          item.datetime_from,
                          item.datetime_to
                        );

                        const isFullDay =
                          moment(
                            item.datetime_from
                          ).format('HH:mm:ss') ===
                            '00:00:00' &&
                          moment(item.datetime_to).format(
                            'HH:mm:ss'
                          ) === '23:59:59';

                        return (
                          <Fragment key={item.id}>
                            <tr>
                              <td className="border-t border-gray-200 relative py-4 pl-4 pr-3 text-sm sm:pl-6">{item.justification?.justification?.code}</td>

                              <td className="border-t border-gray-200 relative py-4 pl-4 pr-3 text-sm sm:pl-6">{item.justification?.justification?.description}</td>

                              <td className="border-t border-gray-200 relative py-4 pl-4 pr-3 text-sm sm:pl-6">
                                {isFullDay ? moment(item.datetime_from).format('DD/MM/YYYY') : moment(item.datetime_from).format('DD/MM/YYYY HH:mm')}
                              </td>
                              <td className="border-t border-gray-200 relative py-4 pl-4 pr-3 text-sm sm:pl-6">
                                {`${endDate} ${endTime}`}
                              </td>
                              <td className="border-t border-gray-200 text-center relative py-4 pl-4 pr-3 text-sm sm:pl-6">
                                {item.status === 'approved' ? (
                                  <CheckCircleIcon className="w-5 h-5 text-teal-500 mx-auto" />
                                ) : item.status === 'rejected' ? (
                                  <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                                ) : (
                                  <div>In attesa di approvazione</div>
                                )}
                              </td>
                            </tr>

                            {item.note?.trim() && (
                              <tr className="border-b border-gray-200 bg-indigo-50">
                                <td
                                  colSpan={5}
                                  className="py-3 px-6 text-gray-600 text-sm text-left"
                                >
                                  <span className="font-semibold text-gray-700">
                                    Note:{' '}
                                  </span>

                                  {item.note}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}

                      <tr className="bg-gray-50 sticky bottom-0 z-10">
                        <td colSpan={5} className="py-4">
                          <div className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={handlePreviousPage}
                              disabled={currentPage === 1}
                              className={`border rounded-md shadow-sm text-sm font-semibold text-center px-3 py-1 ml-4 ${
                                currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-200'
                              }`}
                            >
                              Pagina precedente
                            </button>

                            <div className="text-sm font-semibold text-gray-800 px-2">
                              Pagina {currentPage} di{' '} {totalPages}
                            </div>

                            <button
                              type="button"
                              onClick={handleNextPage}
                              disabled={currentPage === totalPages}
                              className={`border rounded-md shadow-sm text-sm font-semibold text-center px-3 py-1 mr-4 ${
                                currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-200'
                              }`}
                            >
                              Pagina successiva
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default ArNewJustificationRequest;